/**
 * contexts/AuthContext.tsx
 * ------------------------------------------------------------------
 * Authentication, profile, session timeout, and password policy.
 * Ported from v1 to TypeScript; role hierarchy extended for the LKC
 * model (admin > headteacher > editor/hoy > classTeacher >
 * subjectTeacher).
 * ------------------------------------------------------------------
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@/lib/firebase';
import type { AppUser, Role } from '@/types';
import { getPermissions, type Permissions } from '@/lib/permissions';
import {
  STAFF_IDLE_MS, NO_IDLE_LIMIT, idleLimitFor, idleState,
  readActivity, writeActivity, clearActivity, sessionHeld,
} from '@/lib/sessionIdle';

/* ============================ config ============================ */

const SECURITY_CONFIG = {
  /* Signing out after inactivity. The rule and its reasoning live in
     lib/sessionIdle: 10 minutes for staff, 30 for parents, decided by the
     CLOCK rather than by a timer inside the page — see that file for why a
     timer was not enough. */
  SESSION_TIMEOUT_STAFF: STAFF_IDLE_MS,
  SESSION_TIMEOUT_PARENT: NO_IDLE_LIMIT, // parents are not signed out
  PASSWORD_REQUIREMENTS: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSpecial: false,
  },
  MAX_LOGIN_ATTEMPTS: 5,
} as const;

export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
  strength: 'none' | 'weak' | 'medium' | 'strong';
  score: number;
}

export function validatePassword(password: string): PasswordValidation {
  const errors: string[] = [];
  const R = SECURITY_CONFIG.PASSWORD_REQUIREMENTS;
  if (!password) {
    return { isValid: false, errors: ['Password is required'], strength: 'none', score: 0 };
  }
  if (password.length < R.minLength)
    errors.push(`Password must be at least ${R.minLength} characters`);
  if (R.requireUppercase && !/[A-Z]/.test(password))
    errors.push('Password must contain an uppercase letter');
  if (R.requireLowercase && !/[a-z]/.test(password))
    errors.push('Password must contain a lowercase letter');
  if (R.requireNumber && !/[0-9]/.test(password))
    errors.push('Password must contain a number');
  if (R.requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password))
    errors.push('Password must contain a special character');

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

  const strength = score >= 5 ? 'strong' : score >= 3 ? 'medium' : 'weak';
  return { isValid: errors.length === 0, errors, strength, score };
}

export function getPasswordRequirements(): string[] {
  const R = SECURITY_CONFIG.PASSWORD_REQUIREMENTS;
  const out = [`At least ${R.minLength} characters`];
  if (R.requireUppercase) out.push('One uppercase letter (A-Z)');
  if (R.requireLowercase) out.push('One lowercase letter (a-z)');
  if (R.requireNumber) out.push('One number (0-9)');
  if (R.requireSpecial) out.push('One special character (!@#$%^&*)');
  return out;
}

/* ============================ audit ============================ */

async function logAuditEvent(eventData: Record<string, unknown>): Promise<void> {
  try {
    const fn = httpsCallable(functions, 'logAuditEvent');
    await fn(eventData);
  } catch {
    /* audit logging must never block a user action */
  }
}

/* ============================ context ============================ */

interface AuthContextValue {
  currentUser: FirebaseUser | null;
  userRole: Role | null;
  userRoles: Role[];
  permissions: Permissions;
  userData: AppUser | null;
  loading: boolean;
  profileLoading: boolean;
  login: (email: string, password: string) => Promise<FirebaseUser>;
  logout: () => Promise<void>;
  signup: (
    email: string,
    password: string,
    displayName: string,
    role?: Role,
  ) => Promise<FirebaseUser>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
  hasRole: (required: Role) => boolean;
  isAdmin: () => boolean;
  logAuditEvent: (e: Record<string, unknown>) => Promise<void>;
  /** Signed out because the account is deactivated, not because the
   *  password was wrong. The login screen says so. */
  accessBlocked: boolean;
  sessionExpiring: boolean;
  /** When this session runs out, for the warning countdown. */
  sessionExpiresAt: number | null;
  /** The inactivity limit that applies to this person. */
  idleLimitMs: number;
  sessionExpired: boolean;
  extendSession: () => void;
  validatePassword: typeof validatePassword;
  getPasswordRequirements: typeof getPasswordRequirements;
  SECURITY_CONFIG: typeof SECURITY_CONFIG;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

const ROLE_HIERARCHY: Record<Role, number> = {
  systemAdmin: 7,
  admin: 6,
  academicAdmin: 5,
  superAcademicAdmin: 5,
  operationsAdmin: 5,
  headteacher: 5,
  editor: 4,
  hoy: 4,
  // Seniority only, for coarse hasRole() checks. What a housemaster may
  // actually see is decided by the permission grid and their house list.
  boardingMaster: 3,
  hod: 4,
  hrAdmin: 3,
  financeAdmin: 3,
  hr: 3,
  bursar: 3,
  payrollOfficer: 2,
  hrOfficer: 2,
  feesOfficer: 2,
  examsOfficer: 2,
  classTeacher: 2,
  bursar_assistant: 2,
  subjectTeacher: 1,
  admissions: 1,
  parent: 0,
  staff: 0,
  media: 1,
  student: 0,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userRole, setUserRole] = useState<Role | null>(null);
  const [userData, setUserData] = useState<AppUser | null>(null);
  /** Signed out because the account is deactivated — so the login screen can
   *  say why rather than looking like a wrong password. */
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [sessionExpiring, setSessionExpiring] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  /** When this session runs out, so the warning can count down to it. Shared
   *  by every tab, because activity in one keeps the others alive. */
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

  /* This tab's own latest activity; the stored value is every tab's. A
     session is alive while either is recent. */
  const lastActivityRef = useRef<number>(0);
  /** Set by login() so the auth listener knows this is a fresh sign-in and
   *  not a session restored from a visit days ago. */
  const freshSignInRef = useRef(false);

  const rolesForIdle: string[] = userData?.roles && userData.roles.length
    ? userData.roles
    : userRole ? [userRole] : [];
  const idleLimitMs = idleLimitFor(rolesForIdle);
  const idleLimitRef = useRef(idleLimitMs);
  idleLimitRef.current = idleLimitMs;

  const expireSession = useCallback(async (user: FirebaseUser, idleForMs: number) => {
    setSessionExpiring(false);
    setSessionExpiresAt(null);
    await logAuditEvent({
      action: 'session_timeout',
      category: 'auth',
      targetId: user.uid,
      targetType: 'user',
      details: {
        email: user.email,
        idleMinutes: Math.round(idleForMs / 60000),
        limitMinutes: Math.round(idleLimitRef.current / 60000),
      },
    });
    clearActivity();
    setSessionExpired(true);
    try {
      await signOut(auth);
    } catch {
      /* ignore */
    }
    setUserRole(null);
    setUserData(null);
  }, []);

  const extendSession = useCallback(() => {
    if (!currentUser) return;
    const now = Date.now();
    lastActivityRef.current = now;
    writeActivity(currentUser.uid, now);
    setSessionExpiring(false);
    void logAuditEvent({
      action: 'session_extended',
      category: 'auth',
      targetId: currentUser.uid,
      targetType: 'user',
      details: { email: currentUser.email },
    });
  }, [currentUser]);

  const profileReady = !!userData && !!currentUser && userData.uid === currentUser.uid;

  useEffect(() => {
    /* Parents have no limit, so nothing watches them: no interval, no
       listeners, no storage writes — the portal behaves for them exactly as
       it did before any of this existed. */
    if (!currentUser || !profileReady || !Number.isFinite(idleLimitMs)) {
      setSessionExpiring(false);
      setSessionExpiresAt(null);
      return undefined;
    }
    const user = currentUser;
    let done = false;
    let lastWrite = 0;

    const latest = () => Math.max(lastActivityRef.current, readActivity(user.uid) ?? 0);

    const check = () => {
      if (done) return;
      // A held session (the exam room screen with a paper running) counts as
      // active, and says so to the other tabs.
      if (sessionHeld()) {
        const now = Date.now();
        lastActivityRef.current = now;
        if (now - lastWrite > 5000) { lastWrite = now; writeActivity(user.uid, now); }
      }
      const last = latest();
      const limit = idleLimitRef.current;
      const state = idleState(Date.now(), last, limit);
      if (state === 'expired') {
        done = true;
        void expireSession(user, Date.now() - last);
        return;
      }
      setSessionExpiring(state === 'warning');
      setSessionExpiresAt(last + limit);
    };

    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      // Shared with other tabs a few seconds at a time, not on every event.
      if (now - lastWrite > 5000) {
        lastWrite = now;
        writeActivity(user.uid, now);
      }
    };

    /* Judge the session BEFORE listening: the first mouse movement on a
       laptop reopened the next morning must not count as the activity that
       saves it. */
    check();
    if (done) return undefined;

    const events = ['mousedown', 'keydown', 'scroll', 'wheel', 'touchstart', 'pointerdown', 'input', 'mousemove'];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true, capture: true }));
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    const tick = window.setInterval(check, 5000);

    return () => {
      done = true;
      events.forEach((e) => window.removeEventListener(e, onActivity, { capture: true }));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.clearInterval(tick);
    };
  }, [currentUser, profileReady, idleLimitMs, expireSession]);

  /** Returns the profile it loaded, so a caller can act on the roles
   *  immediately — `userData` is state and is still stale at that point. */
  async function fetchProfile(uid: string): Promise<AppUser | null> {
    setProfileLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) {
        const data = snap.data() as AppUser;

        /* THE SECOND LINE. setUserRole disables the Firebase Auth account
           when somebody is deactivated, which is the real lock; this
           refuses the session even if that did not happen — an account
           deactivated before the server-side lock existed, a direct edit
           to the document, a disable that failed halfway.
           
           STRICTLY `=== false`. Most user documents predate this field
           entirely, and treating a missing flag as inactive would lock
           every one of them out of the school on the next refresh. */
        if (data.isActive === false) {
          setUserRole(null);
          setUserData(null);
          setAccessBlocked(true);
          await signOut(auth);
          return null;
        }

        setAccessBlocked(false);
        setUserRole(data.role);
        setUserData({ ...data, id: uid, uid });
        return data;
      }
      return null;
    } catch (e) {
      console.error('Error fetching profile:', e);
      setUserRole(null);
      setUserData(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }

  async function signup(
    email: string,
    password: string,
    displayName: string,
    role: Role = 'subjectTeacher',
  ): Promise<FirebaseUser> {
    const v = validatePassword(password);
    if (!v.isValid) throw new Error(v.errors.join('. '));
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    await setDoc(doc(db, 'users', cred.user.uid), {
      uid: cred.user.uid,
      email,
      displayName,
      role,
      createdAt: new Date().toISOString(),
      isActive: true,
    });
    return cred.user;
  }

  async function login(email: string, password: string): Promise<FirebaseUser> {
    setUserRole(null);
    setUserData(null);
    freshSignInRef.current = true;
    const cred = await signInWithEmailAndPassword(auth, email, password);
    setSessionExpired(false);
    // Signing in IS activity; start the shared clock from here.
    lastActivityRef.current = Date.now();
    writeActivity(cred.user.uid, lastActivityRef.current);
    await fetchProfile(cred.user.uid);
    await setDoc(
      doc(db, 'users', cred.user.uid),
      { lastLoginAt: new Date().toISOString() },
      { merge: true },
    );
    // Login-success audit. (Failed logins are not captured here — the caller
    // isn't authenticated; use Firebase Auth logs for failed-sign-in forensics.)
    await logAuditEvent({
      action: 'login',
      category: 'auth',
      targetId: cred.user.uid,
      targetType: 'user',
      details: { email: cred.user.email },
    });
    return cred.user;
  }

  async function logout(): Promise<void> {
    clearActivity();
    if (currentUser) {
      await logAuditEvent({
        action: 'logout',
        category: 'auth',
        targetId: currentUser.uid,
        targetType: 'user',
        details: { email: currentUser.email, reason: 'user_initiated' },
      });
    }
    setUserRole(null);
    setUserData(null);
    setSessionExpired(false);
    setSessionExpiring(false);
    await signOut(auth);
  }

  async function resetPassword(email: string): Promise<void> {
    // Prefer a continue-URL back to this app, but if the current origin isn't
    // on Firebase Auth's authorized-domains list the SDK throws
    // `auth/unauthorized-continue-uri` and NO email is sent — which silently
    // broke "Forgot password" for everyone on the custom domain. Fall back to
    // a plain reset (Firebase's default hosted handler) so the email always
    // goes out. (Also add the domain under Auth → Settings → Authorized domains.)
    try {
      await sendPasswordResetEmail(auth, email, { url: window.location.origin });
    } catch (err) {
      if ((err as { code?: string })?.code === 'auth/unauthorized-continue-uri') {
        await sendPasswordResetEmail(auth, email);
      } else {
        throw err;
      }
    }
    await logAuditEvent({
      action: 'password_reset',
      category: 'auth',
      targetType: 'user',
      details: { email, requestedAt: new Date().toISOString() },
    });
  }

  async function changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    if (!currentUser) throw new Error('No user logged in');
    const v = validatePassword(newPassword);
    if (!v.isValid) {
      const err = new Error(v.errors.join('. ')) as Error & { code?: string };
      err.code = 'auth/weak-password';
      throw err;
    }
    const credential = EmailAuthProvider.credential(
      currentUser.email ?? '',
      currentPassword,
    );
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
    await setDoc(
      doc(db, 'users', currentUser.uid),
      { passwordChangedAt: new Date().toISOString() },
      { merge: true },
    );
    await logAuditEvent({
      action: 'password_change',
      category: 'auth',
      targetId: currentUser.uid,
      targetType: 'user',
      details: { changedAt: new Date().toISOString() },
    });
  }

  function hasRole(required: Role): boolean {
    if (!userRole) return false;
    return (ROLE_HIERARCHY[userRole] ?? 0) >= (ROLE_HIERARCHY[required] ?? 0);
  }

  function isAdmin(): boolean {
    return userRole === 'admin';
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const fresh = freshSignInRef.current;
        freshSignInRef.current = false;
        const stored = readActivity(user.uid);
        if (fresh || stored === null) {
          // A new sign-in, or the first visit since this check existed.
          lastActivityRef.current = Date.now();
          writeActivity(user.uid, lastActivityRef.current);
        } else {
          /* Whether this is too old depends on the role, and the role is not
             known until the profile loads a moment from now — so the check
             happens there, still before any screen renders. There is no
             limit that applies to everybody any more: parents have none. */
          lastActivityRef.current = stored;
        }
        setCurrentUser(user);
        if (!userData || userData.uid !== user.uid) {
          const profile = await fetchProfile(user.uid);
          /* Between the two limits it depends on the role, which is only
             known once the profile is in. A member of staff who left the
             browser open overnight is signed out here. Read from the
             RETURNED profile: `userData` is state and is still stale. */
          const roles = profile?.roles?.length
            ? profile.roles
            : profile?.role ? [profile.role] : [];
          if (!fresh && stored !== null && Date.now() - stored >= idleLimitFor(roles)) {
            setCurrentUser(null);
            await expireSession(user, Date.now() - stored);
            setLoading(false);
            return;
          }
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userRoles: Role[] = userData?.roles && userData.roles.length
    ? userData.roles
    : userRole ? [userRole] : [];
  const permissions = getPermissions(userRoles);

  const value: AuthContextValue = {
    currentUser,
    userRole,
    userRoles,
    permissions,
    userData,
    loading,
    profileLoading,
    login,
    logout,
    signup,
    resetPassword,
    changePassword,
    hasRole,
    isAdmin,
    logAuditEvent,
    accessBlocked,
    sessionExpiring,
    sessionExpiresAt,
    idleLimitMs,
    sessionExpired,
    extendSession,
    validatePassword,
    getPasswordRequirements,
    SECURITY_CONFIG,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
